import DatePicker from "@/components/form/DatePicker";
import Dropdown, { Item } from "@/components/form/Dropdown";
import TextInput from "@/components/form/TextInput";
import { CreateStaffInfo, StaffInfo } from "@/models/staff-info.model";
import React, { useEffect, useState } from "react";
import { nationalities } from "@/api/countries";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CustomDatePicker } from "@/components/form/CustomDatePicker";

export const genderOptions: Item[] = ["Male", "Female"].map((x) => ({
  label: x,
  value: x,
}));

export const raceOptions: Item[] = [
  "Chinese",
  "Eurasian",
  "Malay",
  "Indian",
  "Others",
].map((x) => ({
  label: x,
  value: x,
}));

export const nationalityOptions: Item[] = nationalities.map((x) => ({
  label: x,
  value: x,
}));

interface Props {
  staffInfo: CreateStaffInfo | null;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setStaffInfo: React.Dispatch<React.SetStateAction<CreateStaffInfo | null>>;
  isReadOnly?: boolean;
}

function StaffInfoForm({
  staffInfo,
  setStaffInfo,
  setPage,
  isReadOnly,
}: Props) {
  // Initialize state with staffInfo values if they exist
  const [gender, setGender] = useState<string>(
    staffInfo?.gender || genderOptions[0].value
  );
  const [nric, setNric] = useState<string>(staffInfo?.nric || "");
  const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(
    staffInfo?.dateOfBirth || undefined
  );
  const [race, setRace] = useState<string>(staffInfo?.race || "");
  const [nationality, setNationality] = useState<string>(
    staffInfo?.nationality || ""
  );
  const [err, setErr] = useState<string>("");

  // Update parent state whenever form fields change
  useEffect(() => {
    if (isValid()) {
      setStaffInfo({
        gender,
        nric,
        dateOfBirth: dateOfBirth || new Date(),
        race,
        nationality,
      });
    }
  }, [gender, nric, dateOfBirth, race, nationality]);

  const isValid = () => {
    if (
      nric.length === 0 ||
      race.length === 0 ||
      nationality.length === 0 ||
      !dateOfBirth
    ) {
      setErr("Please fill in all compulsory fields.");
      return false;
    }
    setErr("");
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid()) {
      return;
    }
    // Form is valid and parent state is already updated via useEffect
    setPage((prev) => prev + 1);
  };

  return (
    <form className="w-full h-full flex flex-col gap-4" onSubmit={handleSubmit}>
      <Dropdown
        label="Gender"
        items={genderOptions}
        setSelectedString={setGender}
        selectedString={gender}
        isReadOnly={isReadOnly}
      />
      <TextInput
        type="text"
        label="NRIC/FIN *"
        value={nric}
        setValue={setNric}
        readOnly={isReadOnly}
        capitalize={true}
      />
      <CustomDatePicker
        date={dateOfBirth}
        setDate={(v) => setDateOfBirth(v)}
        label="Date of Birth *"
        disabled={isReadOnly}
      />
      <div className="flex w-full justify-between gap-2">
        <Dropdown
          label="Nationality *"
          items={nationalityOptions}
          setSelectedString={setNationality}
          selectedString={nationality}
          isReadOnly={isReadOnly}
        />
        <Dropdown
          label="Race *"
          items={raceOptions}
          setSelectedString={setRace}
          selectedString={race}
          isReadOnly={isReadOnly}
        />
      </div>

      {err && <div className="text-red-500 text-sm">{err}</div>}

      <div className="flex w-full justify-end">
        <Button type="submit" disabled={err.length > 0}>
          <ChevronRight />
        </Button>
      </div>
    </form>
  );
}

export default StaffInfoForm;
